// «Mis cobros» del vendedor (2026-11-01): lo que recibió en efectivo, cuánto tiene que entregar en
// tesorería y cada comprobante para volver a compartirlo. Con señal (lo guarda el ERP, no el celular).
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api, ErrorApi } from '../api/cliente';
import { useSesion } from '../sesion/SesionContexto';
import type { VistaRecepcion } from '../tesoreria/comprobanteRuta';
import type { Paquete } from './almacen';
import { useCola } from './cola';
import { pesos } from './logica';
import { VisorComprobante } from './VisorComprobante';

const ESTADO: Record<VistaRecepcion['estado'], { texto: string; tono: string }> = {
  PENDIENTE: { texto: 'por entregar', tono: 'ambar' },
  INGRESADA: { texto: 'ingresado', tono: 'verde' },
  ANULADA: { texto: 'anulado', tono: 'rojo' },
};

export function MisCobros({ paquete }: { paquete: Paquete }) {
  const s = useSesion();
  const cola = useCola();
  const enLinea = s.estado === 'autenticado' && cola.enLinea;
  const [ver, setVer] = useState<VistaRecepcion | null>(null);
  const q = useQuery({
    queryKey: ['vendedor', 'cobros', paquete.empresaId],
    queryFn: () =>
      api.get<VistaRecepcion[]>('/vendedor/cobros', { query: { empresaId: paquete.empresaId } }),
    enabled: enLinea,
    refetchInterval: 60_000,
  });
  const cobros = q.data ?? [];
  const porEntregar = cobros.filter((x) => x.estado === 'PENDIENTE');
  const totalPorEntregar = porEntregar.reduce((t, x) => t + Number(x.total), 0);
  if (!enLinea && !q.data) return null;
  return (
    <section className="caja">
      <div className="caja-tit">
        <h3>Cobros en efectivo</h3>
        {porEntregar.length > 0 && (
          <span className="chip ambar">{porEntregar.length} por entregar</span>
        )}
      </div>
      {q.isError && !(q.error instanceof ErrorApi && q.error.status === 0) && (
        <div className="error">{q.error.message}</div>
      )}
      {porEntregar.length > 0 ? (
        <div className="aviso" style={{ marginBottom: 10 }}>
          Tienes <b>{pesos(totalPorEntregar)}</b> por entregar en tesorería.
        </div>
      ) : (
        <p className="h-sub">Todo lo cobrado ya está ingresado en tesorería.</p>
      )}
      {cobros.map((x) => {
        const e = ESTADO[x.estado];
        return (
          <button
            key={x.recepcionId}
            type="button"
            className="ped cobro-fila"
            onClick={() => setVer(x)}
          >
            <div className="cuerpo">
              <div className="nom">
                {x.numero} · {x.cliente.razonSocial}
              </div>
              <div className="meta">
                {new Date(x.recibidoEn).toLocaleString('es-CL', {
                  dateStyle: 'short',
                  timeStyle: 'short',
                })}{' '}
                · Ver comprobante
              </div>
            </div>
            <span className={`chip ${e.tono}`}>{e.texto}</span>
            <div className="tot">{pesos(Number(x.total))}</div>
          </button>
        );
      })}
      {ver && <VisorComprobante v={ver} alCerrar={() => setVer(null)} />}
    </section>
  );
}
